import React, { useCallback, useId, useMemo, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import {
  Button,
  ConfirmationDialog,
  DatePicker,
  DropdownField,
  Label,
} from '@roger/ui-components';

import { createMedicationOrder } from '../../services/api';
import type {
  ClinicPhysicianResponse,
  OrderResponse,
  PatientMedicationResponse,
} from '../../services/api/types.gen';
import { formatDateToISO, parseISOToLocalDate } from '../../utils/dateUtils';
import { formatPhysicianName } from '../../utils/formatting';
import MedicationsTable from './MedicationsTable';
import {
  getDefaultMedicationOrderDate,
  getMedicationOrderCreateErrorMessage,
  getMedicationOrderDateBounds,
  getMedicationOrderDateDisabledReason,
  getMedicationOrderEligibility,
} from './medicationOrderUtils';

interface MedicationOrderGenerationFlowProps {
  patientId: string;
  medications: PatientMedicationResponse[];
  physicians: ClinicPhysicianResponse[];
  defaultClinicPhysicianId: string | null;
  /** Episode the order is created against — the one launch validation used. */
  episodeId?: string | null;
  episodeStartDate?: string | null;
  episodeEndDate?: string | null;
  /**
   * Date launch validation evaluated the medication-candidate gate at. Preferred
   * over recomputing locally so the flow opens on the same date the server used
   * to decide it could open at all.
   */
  defaultOrderDate?: string | null;
  onCancel: () => void;
  onCreated: (order: OrderResponse) => void;
}

const NO_ACTIVE_MEDICATIONS_REASON =
  'No medications are active on this order date.';
const NO_SELECTION_REASON =
  'Select at least one active medication to create the order.';
const INELIGIBLE_SELECTION_REASON =
  'Selected medications must be active on the order date.';
const MISSING_PHYSICIAN_REASON =
  'Choose a physician to create the medication order.';

export const MedicationOrderGenerationFlow: React.FC<
  MedicationOrderGenerationFlowProps
> = ({
  patientId,
  medications,
  physicians,
  defaultClinicPhysicianId,
  episodeId,
  episodeStartDate,
  episodeEndDate,
  defaultOrderDate,
  onCancel,
  onCreated,
}) => {
  const physicianFieldId = useId();
  const [orderDate, setOrderDate] = useState(
    () =>
      defaultOrderDate ??
      getDefaultMedicationOrderDate(episodeStartDate, episodeEndDate)
  );
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedPhysicianId, setSelectedPhysicianId] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);

  const physicianIds = useMemo(
    () => new Set(physicians.map(physician => physician.id)),
    [physicians]
  );

  React.useEffect(() => {
    setSelectedPhysicianId(current => {
      if (current && physicianIds.has(current)) {
        return current;
      }
      if (
        defaultClinicPhysicianId &&
        physicianIds.has(defaultClinicPhysicianId)
      ) {
        return defaultClinicPhysicianId;
      }
      return '';
    });
  }, [defaultClinicPhysicianId, physicianIds]);

  const selectedIdArray = useMemo(() => Array.from(selectedIds), [selectedIds]);
  const medicationsById = useMemo(
    () => new Map(medications.map(medication => [medication.id, medication])),
    [medications]
  );

  const eligibleMedicationCount = useMemo(
    () =>
      medications.filter(
        medication =>
          getMedicationOrderEligibility(medication, orderDate).eligible
      ).length,
    [medications, orderDate]
  );

  const hasIneligibleSelectedMedication = useMemo(
    () =>
      selectedIdArray.some(medicationId => {
        const medication = medicationsById.get(medicationId);
        return (
          !medication ||
          !getMedicationOrderEligibility(medication, orderDate).eligible
        );
      }),
    [medicationsById, orderDate, selectedIdArray]
  );

  const orderDateBounds = useMemo(
    () => getMedicationOrderDateBounds(episodeStartDate, episodeEndDate),
    [episodeEndDate, episodeStartDate]
  );

  const createDisabledReason = (() => {
    const orderDateReason = getMedicationOrderDateDisabledReason(
      orderDate,
      episodeStartDate,
      episodeEndDate
    );
    if (orderDateReason) return orderDateReason;
    if (eligibleMedicationCount === 0) return NO_ACTIVE_MEDICATIONS_REASON;
    if (selectedIdArray.length === 0) return NO_SELECTION_REASON;
    if (hasIneligibleSelectedMedication) return INELIGIBLE_SELECTION_REASON;
    if (!selectedPhysicianId) return MISSING_PHYSICIAN_REASON;
    return null;
  })();
  const canCreateOrder = !createLoading && !createDisabledReason;

  const selectionState = useMemo(
    () => ({
      selectedIds,
      getEligibility: (medication: PatientMedicationResponse) =>
        getMedicationOrderEligibility(medication, orderDate),
      onToggle: (medicationId: string, checked: boolean) => {
        setSelectedIds(previous => {
          const next = new Set(previous);
          if (checked) {
            next.add(medicationId);
          } else {
            next.delete(medicationId);
          }
          return next;
        });
      },
    }),
    [orderDate, selectedIds]
  );

  const handleCreate = useCallback(async () => {
    if (!canCreateOrder) return;

    setCreateLoading(true);
    setInlineError(null);
    try {
      const response = await createMedicationOrder({
        path: { patient_id: patientId },
        body: {
          order_date: orderDate,
          selected_patient_medication_ids: selectedIdArray,
          clinic_physician_id: selectedPhysicianId,
          ...(episodeId ? { episode_id: episodeId } : {}),
        },
      });

      if (response.error || !response.data) {
        console.error('Medication order create failed', response.error);
        setInlineError(getMedicationOrderCreateErrorMessage(response));
        return;
      }

      onCreated(response.data);
    } catch (error) {
      console.error('Medication order create failed', error);
      setInlineError(getMedicationOrderCreateErrorMessage(error));
    } finally {
      setCreateLoading(false);
    }
  }, [
    canCreateOrder,
    episodeId,
    onCreated,
    orderDate,
    patientId,
    selectedIdArray,
    selectedPhysicianId,
  ]);

  const handleCancel = useCallback(() => {
    if (selectedIds.size > 0) {
      setConfirmCancelOpen(true);
      return;
    }
    onCancel();
  }, [onCancel, selectedIds.size]);

  const handleHiddenMedicationAction = useCallback(() => undefined, []);

  const handleHiddenMedicationDiscontinue = useCallback(
    async () => undefined,
    []
  );

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap items-end justify-between gap-3'>
        <div className='flex flex-wrap items-end gap-3'>
          <div className='space-y-1.5'>
            <Label>Order Date</Label>
            <DatePicker
              date={parseISOToLocalDate(orderDate)}
              onDateChange={date =>
                setOrderDate(date ? formatDateToISO(date) : '')
              }
              minDate={orderDateBounds.minDate}
              maxDate={orderDateBounds.maxDate}
              datePickerSize='sm'
            />
          </div>
          <div className='w-[280px]'>
            <DropdownField
              label='Physician'
              htmlFor={physicianFieldId}
              variant='formSelect'
              sortOptions
              fieldSize='sm'
              placeholder='Select physician'
              options={physicians.map(physician => ({
                value: physician.id,
                label: formatPhysicianName(physician),
              }))}
              value={selectedPhysicianId}
              onChange={setSelectedPhysicianId}
              disabled={createLoading}
            />
          </div>
        </div>
        <div className='flex items-center gap-2'>
          <Button variant='outline' size='sm' onClick={handleCancel}>
            Cancel
          </Button>
          <Button size='sm' onClick={handleCreate} disabled={!canCreateOrder}>
            {createLoading && <Loader2 className='h-4 w-4 mr-2 animate-spin' />}
            Create Medication Order
          </Button>
        </div>
      </div>

      {inlineError && (
        <p className='flex items-center gap-1.5 text-sm text-destructive'>
          <AlertCircle className='h-4 w-4' />
          {inlineError}
        </p>
      )}

      {createDisabledReason && (
        <div
          role='status'
          className='flex items-center gap-2 rounded-md border border-border/60 bg-muted/20 px-3 py-2 text-sm text-muted-foreground'
        >
          <AlertCircle className='h-4 w-4 shrink-0' />
          <span>{createDisabledReason}</span>
        </div>
      )}

      <MedicationsTable
        medications={medications}
        onEdit={handleHiddenMedicationAction}
        onDelete={handleHiddenMedicationAction}
        onDiscontinue={handleHiddenMedicationDiscontinue}
        selection={selectionState}
      />

      <ConfirmationDialog
        open={confirmCancelOpen}
        title='Discard selected medications?'
        message='Your selected medications will be discarded.'
        confirmLabel='Discard Selection'
        cancelLabel='Keep Selecting'
        onAccept={() => {
          setConfirmCancelOpen(false);
          onCancel();
        }}
        onCancel={() => setConfirmCancelOpen(false)}
      />
    </div>
  );
};

export default MedicationOrderGenerationFlow;
